import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Building2, Users, Plus, X, Save, Edit2, Trash2, Loader2, AlertCircle, RefreshCw, LayoutGrid, List, UserCheck, ChevronDown, CheckCircle, Briefcase, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { departmentService, employeeService } from '../services';
import type { Department, DepartmentCreateDto, DepartmentUpdateDto } from '../services/departmentService';
import siteService from '../services/siteService';
import locationService from '../services/locationService';
import { uyar, hata, onayla } from '../components/GlobalModal';

interface Employee {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  position?: string;
  positionName?: string;
  positionTitle?: string;
  email?: string;
  phone?: string;
  departmentId?: number;
  isActive?: boolean;
}

const Organization = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');

  // Employee list modal
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [employeesModalDept, setEmployeesModalDept] = useState<Department | null>(null);
  const [deptEmployees, setDeptEmployees] = useState<Employee[]>([]);
  const [loadingDeptEmployees, setLoadingDeptEmployees] = useState(false);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [formData, setFormData] = useState<DepartmentCreateDto>({
    name: '',
    description: '',
    managerId: undefined,
    siteId: null,
    locationId: null
  });
  const [sitesList, setSitesList] = useState<{ id: number; name: string }[]>([]);
  const [locationsList, setLocationsList] = useState<{ id: number; name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    (async () => {
      try {
        const s = await siteService.getDropdown();
        if (s?.success && Array.isArray(s.data)) setSitesList(s.data as any);
      } catch {}
      try {
        const l = await locationService.getForDropdown();
        if (l?.success && Array.isArray(l.data)) setLocationsList(l.data as any);
      } catch {}
    })();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [deptRes, empRes] = await Promise.all([
        departmentService.getAll(),
        employeeService.getAll()
      ]);

      if (deptRes.success) setDepartments(deptRes.data);
      if (empRes.success) {
        const empData = empRes.data as any;
        const empList = empData.items || empData;
        setEmployees(Array.isArray(empList) ? empList : []);
      }
    } catch (err: any) {
      console.error('Load error:', err);
      setError(err.response?.data?.message || t('common.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleShowDeptEmployees = async (dept: Department) => {
    setEmployeesModalDept(dept);
    setShowEmployeesModal(true);
    setLoadingDeptEmployees(true);
    try {
      const deptId = dept.id || dept.departmentId;
      const res = await employeeService.getByDepartment(deptId);
      if (res.success) {
        const data = res.data as any;
        const list = data?.items || data;
        setDeptEmployees(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Dept employees load error:', err);
      setDeptEmployees([]);
    } finally {
      setLoadingDeptEmployees(false);
    }
  };

  const filteredDepartments = useMemo(() => {
    return departments.filter(dept => {
      const searchScope = `${dept.name} ${dept.departmentName} ${dept.description || ''} ${dept.managerName || ''}`.toLowerCase();
      return searchScope.includes(searchTerm.toLowerCase());
    });
  }, [departments, searchTerm]);

  const handleAdd = () => {
    setFormData({ name: '', description: '', managerId: undefined, siteId: null, locationId: null });
    setEditingDept(null);
    setFormError(null);
    setShowModal(true);
  };

  const handleEdit = (dept: Department) => {
    setFormData({
      name: dept.name || dept.departmentName,
      description: dept.description || '',
      managerId: dept.managerId || undefined,
      siteId: dept.siteId ?? null,
      locationId: dept.locationId ?? null
    });
    setEditingDept(dept);
    setFormError(null);
    setShowModal(true);
  };

  const handleDelete = async (dept: Department) => {
    if (!(await onayla(t('organization.confirmDelete', { name: dept.name || dept.departmentName })))) {
      return;
    }

    try {
      const res = await departmentService.delete(dept.id || dept.departmentId);
      if (res.success) {
        setDepartments(prev => prev.filter(d => (d.id || d.departmentId) !== (dept.id || dept.departmentId)));
        setSuccessMessage(t('organization.deleteSuccess'));
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      console.error('Delete error:', err);
      setError(err.response?.data?.message || t('common.error'));
    }
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      setFormError(t('organization.nameRequired'));
      return;
    }

    try {
      setSaving(true);
      setFormError(null);

      if (editingDept) {
        const updateDto: DepartmentUpdateDto = {
          name: formData.name,
          description: formData.description,
          managerId: formData.managerId,
          siteId: formData.siteId ?? null,
          locationId: formData.locationId ?? null
        };
        const res = await departmentService.update(editingDept.id || editingDept.departmentId, updateDto);
        if (res.success) {
          await loadData();
          setShowModal(false);
          setSuccessMessage(t('organization.updateSuccess'));
          setTimeout(() => setSuccessMessage(null), 3000);
        }
      } else {
        const res = await departmentService.create(formData);
        if (res.success) {
          await loadData();
          setShowModal(false);
          setSuccessMessage(t('organization.createSuccess'));
          setTimeout(() => setSuccessMessage(null), 3000);
        }
      }
    } catch (err: any) {
      console.error('Save error:', err);
      setFormError(err.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">{t('common.loading')}</span>
      </div>
    );
  }

  if (error && departments.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">{t('common.error')}</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={loadData} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-[100] bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-in slide-in-from-top duration-300">
          <CheckCircle size={20} />
          <span className="font-medium">{successMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">{t('organization.title')}</h1>
          <p className="text-slate-500 text-sm font-medium">{t('organization.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-medium">
            <RefreshCw size={16} /> {t('common.refresh')}
          </button>
          <button
            onClick={handleAdd}
            className="bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-700 flex items-center gap-2 transition-all shadow-lg shadow-blue-100 active:scale-95"
          >
            <Plus size={18} /> {t('organization.addDepartment')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder={t('organization.searchPlaceholder')}
            className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 text-sm font-medium transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-3">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            {filteredDepartments.length} {t('organization.departments')}
          </span>
          <div className="flex bg-slate-50 border border-slate-200 rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`p-1.5 rounded-md transition-all ${viewMode === 'card' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              title="Kart Gorunumu"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              title="Liste Gorunumu"
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Card View */}
      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDepartments.map(dept => (
            <div key={dept.id || dept.departmentId} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden group hover:shadow-lg hover:border-blue-200 transition-all">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-blue-200">
                      <Building2 size={28} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-800">{dept.name || dept.departmentName}</h3>
                      <p className="text-xs text-slate-400 mt-0.5">{dept.description || t('organization.noDescription')}</p>
                    </div>
                  </div>
                </div>

                {/* Manager */}
                <div className="mb-4">
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{t('organization.departmentManager')}</div>
                  {dept.managerName ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
                      <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center text-white font-bold text-sm">
                        {dept.managerName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </div>
                      <div>
                        <div className="font-bold text-slate-800 text-sm">{dept.managerName}</div>
                        <div className="text-[10px] text-green-600 font-bold uppercase tracking-widest flex items-center gap-1">
                          <UserCheck size={10} /> Yetkili
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-slate-50 rounded-xl border border-dashed border-slate-200 text-slate-400 text-sm text-center">
                      {t('organization.noManager')}
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="flex gap-3">
                  <div
                    className="flex-1 p-3 bg-blue-50 rounded-xl text-center cursor-pointer hover:bg-blue-100 hover:shadow-sm transition-all"
                    onClick={() => handleShowDeptEmployees(dept)}
                    title="Personelleri gor"
                  >
                    <div className="text-2xl font-black text-blue-600">{dept.employeeCount || 0}</div>
                    <div className="text-[9px] font-black text-blue-500 uppercase tracking-widest">{t('organization.personnel')}</div>
                  </div>
                  <div className={`flex-1 p-3 rounded-xl text-center ${dept.isActive ? 'bg-green-50' : 'bg-red-50'}`}>
                    <div className={`text-sm font-black ${dept.isActive ? 'text-green-600' : 'text-red-600'}`}>
                      {dept.isActive ? t('employees.active') : t('employees.inactive')}
                    </div>
                    <div className={`text-[9px] font-black uppercase tracking-widest ${dept.isActive ? 'text-green-500' : 'text-red-500'}`}>{t('common.status')}</div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                <button
                  onClick={() => handleEdit(dept)}
                  className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  title="Duzenle"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(dept)}
                  className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                  title="Sil"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('employees.department')}</th>
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('organization.manager')}</th>
                <th className="text-center px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('organization.personnel')}</th>
                <th className="text-center px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('common.status')}</th>
                <th className="text-right px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredDepartments.map(dept => (
                <tr key={dept.id || dept.departmentId} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">
                        <Building2 size={18} />
                      </div>
                      <div>
                        <div className="font-bold text-slate-800">{dept.name || dept.departmentName}</div>
                        <div className="text-[10px] text-slate-400">{dept.description || t('organization.noDescription')}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {dept.managerName ? (
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center text-green-600 font-bold text-xs">
                          {dept.managerName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </div>
                        <span className="text-sm font-medium text-slate-700">{dept.managerName}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">{t('organization.notAssigned')}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold cursor-pointer hover:bg-blue-100 hover:shadow-sm transition-all"
                      onClick={() => handleShowDeptEmployees(dept)}
                      title="Personelleri gor"
                    >
                      <Users size={12} />
                      {dept.employeeCount || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                      dept.isActive
                        ? 'bg-green-50 text-green-600 border border-green-100'
                        : 'bg-red-50 text-red-600 border border-red-100'
                    }`}>
                      {dept.isActive ? t('employees.active') : t('employees.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEdit(dept)}
                        className="p-2 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Duzenle"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(dept)}
                        className="p-2 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredDepartments.length === 0 && (
        <div className="py-20 text-center">
          <div className="w-20 h-20 bg-slate-100 rounded-[2rem] flex items-center justify-center mx-auto text-slate-300 mb-6">
            <Building2 size={40} />
          </div>
          <h3 className="text-lg font-black text-slate-800">{t('organization.noDepartments')}</h3>
          <p className="text-slate-500 text-sm font-medium mt-2">{t('organization.noSearchResults')}</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-black text-slate-800">{editingDept ? t('organization.editDepartment') : t('organization.addDepartment')}</h2>
                <p className="text-sm text-slate-500 mt-1">{t('organization.enterDetails')}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                  <AlertCircle size={16} />
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{t('organization.departmentName')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-medium"
                  placeholder="Ornek: Bilgi Islem"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{t('organization.description')}</label>
                <textarea
                  rows={3}
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-medium resize-none"
                  placeholder="Departman gorev ve sorumluluklari..."
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{t('organization.departmentManager')}</label>
                <div className="relative">
                  <select
                    value={formData.managerId || ''}
                    onChange={(e) => setFormData({ ...formData, managerId: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-medium appearance-none"
                  >
                    <option value="">{t('organization.selectManager')}</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.fullName || `${emp.firstName} ${emp.lastName}`}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {sitesList.length > 0 && (
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Şantiye / Site</label>
                  <div className="relative">
                    <select
                      value={formData.siteId ?? ''}
                      onChange={(e) => setFormData({ ...formData, siteId: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-medium appearance-none"
                    >
                      <option value="">— Seçim yok —</option>
                      {sitesList.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              )}

              {locationsList.length > 0 && (
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Lokasyon</label>
                  <div className="relative">
                    <select
                      value={formData.locationId ?? ''}
                      onChange={(e) => setFormData({ ...formData, locationId: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-medium appearance-none"
                    >
                      <option value="">— Seçim yok —</option>
                      {locationsList.map(l => (
                        <option key={l.id} value={l.id}>{l.name}</option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-100 transition-all"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {saving ? t('common.saving') : editingDept ? t('common.update') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Department Employees Modal */}
      {showEmployeesModal && employeesModalDept && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setShowEmployeesModal(false)}>
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-blue-200">
                  <Building2 size={24} />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-800">{employeesModalDept.name || employeesModalDept.departmentName}</h2>
                  <p className="text-sm text-slate-500 mt-0.5">{deptEmployees.length} {t('organization.personnel')}</p>
                </div>
              </div>
              <button onClick={() => setShowEmployeesModal(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                <X size={20} className="text-slate-400" />
              </button>
            </div>

            {/* Employee List */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingDeptEmployees ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  <span className="ml-2 text-slate-500 font-medium">{t('common.loading')}</span>
                </div>
              ) : deptEmployees.length === 0 ? (
                <div className="py-12 text-center">
                  <Users size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-400 font-medium">{t('organization.noEmployeesInDept')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {deptEmployees.map(emp => (
                    <div
                      key={emp.id}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-blue-50 hover:border-blue-200 border border-slate-100 transition-all cursor-pointer group"
                      onClick={() => { setShowEmployeesModal(false); navigate(`/employees?highlight=${emp.id}`); }}
                    >
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm group-hover:bg-blue-200 transition-colors">
                        {(emp.fullName || `${emp.firstName} ${emp.lastName}`).split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-800 text-sm">{emp.fullName || `${emp.firstName} ${emp.lastName}`}</div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          {(emp.positionName || emp.positionTitle || emp.position) && (
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-1">
                              <Briefcase size={10} /> {emp.positionName || emp.positionTitle || emp.position}
                            </span>
                          )}
                          {emp.email && (
                            <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                              <Mail size={10} /> {emp.email}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest ${
                        emp.isActive !== false ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'
                      }`}>
                        {emp.isActive !== false ? t('employees.active') : t('employees.inactive')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Organization;
